import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router";
import { toast } from "sonner";

// Map backend error codes to user-friendly messages
const ERROR_MESSAGES: Record<string, string> = {
  SOP_NOT_ALLOWED:
    "Your UAE Pass account type is not supported. Please use a verified UAE Pass account.",
  EMAIL_NOT_CONFIRMED:
    "Please verify your email address before linking UAE Pass.",
  FAILED_TO_LINK_EXTERNAL_LOGIN:
    "Failed to link UAE Pass to your account. Please try again.",
  EMAIL_CHANGED:
    "The email associated with your UAE Pass has changed. Please contact support.",
  EMAIL_MISMATCH:
    "The email in your UAE Pass account doesn't match your account email. Please use a UAE Pass account with the same email.",
  FAILED_TO_CREATE_NEW_USER: "Failed to create your account. Please try again.",
  USER_NOT_FOUND: "Your account was not found. Please try signing in again.",
  UAEPASS_ALREADY_LINKED_TO_ANOTHER_ACCOUNT:
    "This UAE Pass is already linked to another account.",
  UNKNOWN_ERROR:
    "An unexpected error occurred while linking UAE Pass. Please try again.",
};

/**
 * Hook to handle UAE Pass linking errors from URL parameters.
 * When the UAE Pass linking flow fails, the user is redirected back to the
 * original page with a `uaepass_error` query parameter. This hook reads
 * that parameter and displays a user-friendly toast message.
 *
 * Usage: Call this hook in any page where UAE Pass linking can be initiated.
 */
export const useUaePassLinkError = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasHandledError = useRef(false);

  useEffect(() => {
    // Only run once per mount to prevent double-handling in StrictMode
    if (hasHandledError.current) return;

    // Read directly from window.location to ensure we get the initial URL
    // before any React Router navigation might clear it
    const urlParams = new URLSearchParams(window.location.search);
    const errorCode = urlParams.get("uaepass_error");

    // Debug logging - remove after fixing
    console.log("[useUaePassLinkError] Current URL:", window.location.href);
    console.log("[useUaePassLinkError] Search params:", window.location.search);
    console.log("[useUaePassLinkError] Error code:", errorCode);

    if (errorCode) {
      hasHandledError.current = true;

      // Get user-friendly message or use the error code as fallback
      const message =
        ERROR_MESSAGES[errorCode] ||
        `UAE Pass linking failed: ${errorCode.replace(/_/g, " ").toLowerCase()}`;

      // Small delay to ensure Toaster component is mounted
      setTimeout(() => {
        toast.error(message, {
          duration: 6000,
        });
      }, 100);

      // Remove the error param from URL to prevent showing on refresh
      // Use navigate with replace to update React Router state as well
      const newParams = new URLSearchParams(window.location.search);
      newParams.delete("uaepass_error");
      const newSearch = newParams.toString();
      const newUrl = newSearch
        ? `${location.pathname}?${newSearch}`
        : location.pathname;

      navigate(newUrl, { replace: true });
    }
  }, [navigate, location.pathname]);
};
